import imgAssignmentFilled from '../../../images/img_assignment_filled.svg';
import imgBusinessCenterFilled from '../../../images/img_business_center_filled.svg';
// V2 Helpful Tips icons
import imgHelpfulTip1 from '../../../images/img_helpful_tip_1.svg';
import imgHelpfulTip2 from '../../../images/img_helpful_tip_2.svg';
import imgHelpfulTip3 from '../../../images/img_helpful_tip_3.svg';
import imgPersonFilled from '../../../images/img_person_filled.svg';

import {
  type AdvancedFiltersForm,
  type OralArgumentsFiltersForm,
  type JudgeSearchFiltersForm,
} from './types';
import {
  createDefaultSelectedJurisdictions,
  createSelectedJurisdictionsForTab,
} from './utils';

export const FAQ_ITEMS = [
  {
    id: '0',
    question: 'What is the Court Case Search?',
    answer:
      'Court Case Search allows you to look up publicly available federal, state and supreme court records, including details about legal cases, parties involved, case outcomes, and more.',
  },
  {
    id: '1',
    question: 'What types of cases can I find?',
    answer:
      'You can search for a wide range of federal, state, and local cases, including:',
    list: [
      'Civil and criminal cases',
      'Appellate court decisions',
      'U.S. Supreme Court cases',
      'Bankruptcy filings (selected courts)',
    ],
  },
  {
    id: '2',
    question: 'What information will I see in the results?',
    answer: 'Search results may include:',
    list: [
      'Case titles and docket numbers',
      'Court jurisdictions',
      'Names of parties and attorneys involved',
      'Key dates and case summaries',
      'Case outcomes and written opinions (if available)',
    ],
  },
  {
    id: '3',
    question: 'Can I search by name?',
    answer:
      'Yes. You can search using the name of a person, business, or organization involved in a case. More specific searches (e.g., full name, court jurisdiction) may yield better results.',
  },
  {
    id: '4',
    question: 'How current is the information?',
    answer:
      'Court records are updated regularly, but some cases may take time to appear depending on how and when they are filed.',
  },
  {
    id: '5',
    question: "Why can't I find a specific case?",
    answer: 'There could be a few reasons:',
    list: [
      'The case may be sealed or not publicly available',
      'It may be filed in a local/state court not included in the search',
      'Details may be entered differently than expected (e.g., name spelling)',
    ],
  },
];

export const HELPFUL_TIPS = [
  {
    id: '0',
    text: 'Add quotation marks to find exact matches. Try "John A. Smith" instead of John Smith to improve accuracy.',
    icon: imgPersonFilled,
  },
  {
    id: '1',
    text: "You can also search for companies or institutions involved in legal cases. Just enter the organization's full name to see opinions and cases.",
    icon: imgBusinessCenterFilled,
  },
  {
    id: '2',
    text: 'If you have a docket number or case ID, enter it directly for the most precise match.',
    icon: imgAssignmentFilled,
  },
];

// V2 Helpful Tips with new illustrations matching Figma design
export const HELPFUL_TIPS_V2 = [
  {
    id: '0',
    title: 'Tip #1',
    text: 'Use quotes for exact matches. Typing "John A. Smith" improves accuracy compared to John Smith.',
    icon: imgHelpfulTip1,
  },
  {
    id: '1',
    title: 'Tip #2',
    text: 'You can lookup company or institutions involved in legal cases by entering their full name.',
    icon: imgHelpfulTip2,
  },
  {
    id: '2',
    title: 'Tip #3',
    text: 'If you have a docket number or case ID, enter it directly for the most precise match.',
    icon: imgHelpfulTip3,
  },
];

// V2 Search Operators for tooltip/popover
export const SEARCH_OPERATORS = {
  title: 'Using Operators',
  description:
    'Operators can be used to search for complex queries. Pairing with "Advanced Options" can help focus on your desired results.',
  operators: [
    {
      id: 'and',
      symbol: 'AND',
      name: 'Intersection',
      example: 'e.g John AND Johnathan',
      exampleParts: ['e.g John ', 'AND', ' Johnathan'],
    },
    {
      id: 'or',
      symbol: 'OR',
      name: 'Union',
      example: 'e.g Red OR Blue',
      exampleParts: ['e.g Red ', 'OR', ' Blue'],
    },
    {
      id: 'negation',
      symbol: '-',
      name: 'Negation',
      example: 'e.g Whiskey -Bourbon',
      exampleParts: ['e.g Whiskey ', '-', 'Bourbon'],
    },
    {
      id: 'phrase',
      symbol: '" "',
      name: 'Phrase Search',
      example: 'e.g "Selling" products',
      exampleParts: ['e.g ', '"', 'Selling', '"', ' products'],
    },
    {
      id: 'grouped',
      symbol: '()',
      name: 'Grouped Quotes',
      example: 'e.g (Phone OR Cell)',
      exampleParts: ['e.g (Phone ', 'OR', ' Cell)'],
    },
    {
      id: 'field',
      symbol: ':',
      name: 'Field Search',
      example: 'e.g Revenue in: 10k',
      exampleParts: ['e.g Revenue in', ':', ' 10k'],
    },
    {
      id: 'wildcard',
      symbol: '*, ?',
      name: 'Wildcard',
      example: 'e.g fee and law*',
      exampleParts: ['e.g fee and law', '*'],
    },
    {
      id: 'range',
      symbol: '[x TO y]',
      name: 'Fuzzy & Proximity',
      example: 'e.g Harlem TO Astoria',
      exampleParts: ['e.g Harlem ', 'TO', ' Astoria'],
    },
  ],
};

// V2 FAQ Disclaimer
export const FAQ_DISCLAIMER =
  'PLEASE BE ADVISED: Court case information contained in our reports may not always be 100% up-to-date, accurate or complete, since data is pulled from records maintained by various state, county, municipal and other government agencies and their contents may not be fully digitized, updated or comprehensive. Please use our results as no more than a point of departure and certainly no substitute for your own due diligence.';

// No Results: Simple search tips list
export const NO_RESULTS_TIPS = [
  { id: 'spelling', text: 'Check your spelling.' },
  { id: 'filters', text: 'Try fewer filters.' },
  { id: 'quotes', text: 'Remove quotes if you used them.' },
];

// Unified sort options for both header sort and advanced filters
export const SORT_OPTIONS = [
  { value: 'score_desc', display: 'Relevance' },
  { value: 'dateFiled_desc', display: 'Newest Cases First' },
  { value: 'dateFiled_asc', display: 'Oldest Cases First' },
  { value: 'citeCount_desc', display: 'Most Cited First' },
  { value: 'citeCount_asc', display: 'Least Cited First' },
];

// Oral Arguments Sort Options - limited to relevance and date sorting
export const ORAL_ARGUMENTS_SORT_OPTIONS = [
  { value: 'score_desc', display: 'Relevance' },
  { value: 'dateArgued_desc', display: 'Newest First' },
  { value: 'dateArgued_asc', display: 'Oldest First' },
];

// Legacy export for backward compatibility - now points to SORT_OPTIONS
export const SEARCH_ORDER_OPTIONS = SORT_OPTIONS;

// Advanced Filters: Operators sidebar data
export const OPERATORS = [
  { symbol: 'AND', description: 'Intersection' },
  { symbol: 'OR', description: 'Union' },
  { symbol: '-', description: 'Negation' },
  { symbol: '" "', description: 'Phrase search' },
  { symbol: '()', description: 'Grouped queries' },
  { symbol: ':', description: 'Fielded search' },
  { symbol: '*, ?', description: 'Wildcard' },
  { symbol: '~', description: 'Fuzzy and proximity' },
  { symbol: '[x TO y]', description: 'Ranges' },
];

// Advanced Filters: Default values used across the feature
export const ADVANCED_FILTERS_DEFAULTS: AdvancedFiltersForm = {
  case_name: '',
  judge: '',
  docket_number: '',
  party_name: '',
  atty_name: '',
  order_by: 'score_desc',
  jurisdictions: createDefaultSelectedJurisdictions(),
  exact_match: true,
};

// Tooltip texts for toggle
export const TOOLTIP_TEXTS = {
  description:
    'Exact Match: Returns only results that exactly match the search query. Non-exact match: Returns results that contain the search query.',
  toggleText: 'Toggle exact match search',
};

// GA4 tracking map for sort options
export const SORT_GA4_TRACKING_MAP: Record<string, [string, string]> = {
  score_desc: ['sort_relevance', 'relevance'],
  dateFiled_desc: ['sort_most_recent', 'most recent'],
  dateFiled_asc: ['sort_least_recent', 'least recent'],
  citeCount_desc: ['sort_most_cited', 'most cited'],
  citeCount_asc: ['sort_least_cited', 'least cited'],
};

// IDB Field Mapping Constants
export const ORIGIN_MAP: { [key: string]: string } = {
  '1': '(1) Original Proceeding',
  '2': '(2) Removed from State Court',
  '3': '(3) Remanded from Appellate Court',
  '4': '(4) Reinstated or Reopened',
  '5': '(5) Transferred from another District',
  '6': '(6) Multi-District Litigation',
  '7': '(7) Appeal of Magistrate Judgment to a District Judge (since SY81)',
  '8': '(8) 2nd Reopening (since SY83)',
  '9': '(9) 3rd Reopening (since SY83)',
  '-8': '(-8) Missing',
  '0': '(0) Unknown value from 1970-1987 data',
};

export const JURISDICTION_MAP: { [key: string]: string } = {
  '1': '(1) U.S. Plaintiff',
  '2': '(2) U.S. Defendant',
  '3': '(3) Federal Question',
  '4': '(4) Diversity of Citizenship',
  '5': '(5) Local Question',
};

export const NATURE_OF_SUIT_MAP: { [key: string]: string } = {
  '100': '(100) Indemnity of Admiralty Cases',
  '110': '(110) Insurance',
  '112': '(112) Unknown',
  '114': '(114) Unknown',
  '120': '(120) Marine Contract Actions',
  '122': '(122) Unknown',
  '125': '(125) Unknown',
  '130': '(130) Miller Act',
  '140': '(140) Negotiable Instruments',
  '141': '(141) Unknown',
  '150': '(150) Overpayments & Enforcement of Judgments',
  '151': '(151) Overpayments Under the Medicare Act',
  '152': '(152) Recovery of Defaulted Student Loans',
  '153': '(153) Recovery of Overpayments of Vet Benefits',
  '160': "(160) Stockholder's Suits",
  '170': '(170) Unknown',
  '190': '(190) Other Contract Actions',
  '191': '(191) Employee Relations',
  '192': '(192) State Fair Trade Act',
  '193': '(193) Hospital Care Act',
  '195': '(195) Contract Product Liability',
  '210': '(210) Land Condemnation',
  '220': '(220) Foreclosure',
  '230': '(230) Rent, Lease, Ejectment',
  '240': '(240) Torts to Land',
  '241': '(241) Unknown',
  '245': '(245) Tort Product Liability',
  '290': '(290) Other Real Property Actions',
  '310': '(310) Airplane Personal Injury',
  '315': '(315) Airplane Product Liability',
  '320': '(320) Assault, Libel, and Slander',
  '330': "(330) Federal Employers' Liability",
  '340': '(340) Marine Personal Injury',
  '341': '(341) Unknown',
  '345': '(345) Marine - Product Liability',
  '350': '(350) Motor Vehicle Personal Injury',
  '351': '(351) Unknown',
  '355': '(355) Motor Vehicle Product Liability',
  '360': '(360) Other Personal Injury',
  '361': "(361) Workman's Compensation (Industrial Accident Board)",
  '362': '(362) Medical Malpractice',
  '365': '(365) Personal Injury - Product Liability',
  '368': '(368) Asbestos Personal Injury - Product Liability',
  '370': '(370) Other Fraud',
  '371': '(371) Truth in Lending',
  '380': '(380) Other Personal Property Damage',
  '385': '(385) Property Damage - Product Liability',
  '390': '(390) Unknown',
  '400': '(400) State Re-apportionment',
  '410': '(410) Antitrust',
  '416': '(416) Unknown',
  '420': '(420) Trustee',
  '421': '(421) Transfer (915b)',
  '422': '(422) Bankruptcy Appeals Rule 28 USC 158',
  '423': '(423) Bankruptcy Withdrawal 28 USC 157',
  '430': '(430) Banks and Banking',
  '440': '(440) Other Civil Rights',
  '441': '(441) Civil Rights Voting',
  '442': '(442) Civil Rights Jobs',
  '443': '(443) Civil Rights Accommodations',
  '444': '(444) Civil Rights Welfare',
  '450': '(450) Interstate Commerce',
  '451': '(451) Unknown',
  '460': '(460) Deportation',
  '461': '(461) Deportation - Review',
  '470': '(470) Civil (RICO)',
  '510': '(510) Prisoner Petitions -Vacate Sentence',
  '511': '(511) Unknown',
  '520': '(520) Parole Board Review',
  '530': '(530) Prisoner Petitions -Habeas Corpus',
  '531': '(531) Unknown',
  '540': '(540) Prisoner Petitions -Mandamus and Other',
  '550': '(550) Prisoner -Civil Rights',
  '610': '(610) Agricultural Acts',
  '620': '(620) Food and Drug Acts',
  '630': '(630) Liquor Laws',
  '640': '(640) Railroad and Trucks',
  '650': '(650) Airline Regulations',
  '660': '(660) Occupational Safety/Health',
  '690': '(690) Other Forfeiture and Penalty Suits',
  '710': '(710) Fair Labor Standards Act',
  '711': '(711) Unknown',
  '720': '(720) Labor/Management Relations Act',
  '730': '(730) Labor/Management Report & Disclosure',
  '740': '(740) Railway Labor Act',
  '750': '(750) Welfare Pension Plan Disclosure',
  '790': '(790) Other Labor Litigation',
  '791': '(791) Employee Retirement Income Security Act',
  '810': '(810) Selective Service',
  '820': '(820) Copyright',
  '830': '(830) Patent',
  '840': '(840) Trademark',
  '850': '(850) Securities, Commodities, Exchange',
  '860': '(860) Social Security',
  '861': '(861) HIA (1395 FF)/ Medicare',
  '862': '(862) Black Lung',
  '863': '(863) D.I.W.C./D.I.W.W.',
  '864': '(864) S.S.I.D.',
  '865': '(865) R.S.I.',
  '870': '(870) Tax Suits',
  '871': '(871) IRS 3rd Party Suits 26 USC 7609',
  '875': '(875) Customer Challenge 12 USC 3410',
  '876': '(876) Unknown',
  '880': '(880) Review or Enforcement',
  '890': '(890) Other Statutory Actions',
  '891': '(891) Agricultural Acts',
  '892': '(892) Economic Stabilization Act',
  '893': '(893) Environmental Matters',
  '894': '(894) Energy Allocation Act',
  '895': '(895) Freedom of Information Act of 1974',
  '900': '(900) Appeal of Fee -Equal Access to Justice',
  '910': '(910) Domestic Relations',
  '920': '(920) Insanity',
  '930': '(930) Probate',
  '940': '(940) Substitute Trustee',
  '950': '(950) Constitutionality of State Statutes',
  '970': '(970) NARA, Title III',
  '971': '(971) Unknown',
  '990': '(990) Other',
  '991': '(991) Unknown',
  '992': '(992) Local Jurisdictional Appeal',
  '999': '(999) Miscellaneous',
};

export const DISPOSITION_MAP: { [key: string]: string } = {
  '0': '(0) Transfer to Another District',
  '1': '(1) Remanded to State Court',
  '2': '(2) Want of Prosecution',
  '3': '(3) Lack of Jurisdiction',
  '4': '(4) Default',
  '5': '(5) Consent',
  '6': '(6) Motion Before Trial',
  '7': '(7) Jury Verdict',
  '8': '(8) Directed Verdict',
  '9': '(9) Court Trial',
  '10': '(10) Multi District Litigation Transfer',
  '11': '(11) Remanded to U.S. Agency',
  '12': '(12) Voluntarily',
  '13': '(13) Settled',
  '14': '(14) Other',
  '15': '(15) Award of Arbitrator',
  '16': '(16) Stayed Pending Bankruptcy',
  '17': '(17) Other',
  '18': '(18) Statistical Closing',
  '-9': '(-9) Data Not Collected (SY72-SY78)',
};

export const PRO_SE_MAP: { [key: string]: string } = {
  '0': '(0) No Pro Se Plaintiffs or Defendants',
  '1': '(1) Pro Se Plaintiffs, but No Pro Se Defendants',
  '2': '(2) Pro Se Defendants, but No Pro Se Plaintiffs',
  '3': '(3) Both Pro Se Plaintiffs & Defendants',
  '-8': '(-8) Missing',
};

// Attorney role constants based on CourtListener API
export const ATTORNEY_ROLES = {
  ATTORNEY_TO_BE_NOTICED: 1,
  LEAD_ATTORNEY: 2,
  SPECIAL_ROLE: 4, // Additional role seen in some cases
  PRO_SE: 10,
} as const;

export const ATTORNEY_ROLE_DISPLAY_MAP: { [key: number]: string } = {
  [ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]: 'ATTORNEY TO BE NOTICED',
  [ATTORNEY_ROLES.LEAD_ATTORNEY]: 'LEAD ATTORNEY',
  [ATTORNEY_ROLES.SPECIAL_ROLE]: 'LEAD ATTORNEY', // Treat special role as lead attorney
  [ATTORNEY_ROLES.PRO_SE]: 'PRO SE',
};

// ============= ORAL ARGUMENTS =============

export const ORAL_ARGUMENTS_FAQ_ITEMS = [
  {
    id: '0',
    question: 'What are Oral Arguments?',
    answer:
      'Oral arguments are spoken presentations made before a court by attorneys representing the parties in a case. They provide an opportunity for lawyers to present their legal positions and answer questions from judges.',
  },
  {
    id: '1',
    question: 'What types of oral arguments can I find?',
    answer:
      'You can search for oral arguments from federal appellate courts, including:',
    list: [
      'U.S. Supreme Court arguments',
      'U.S. Circuit Courts of Appeals (1st through 11th Circuit)',
      'D.C. Circuit Court of Appeals',
      'Federal Circuit Court of Appeals',
    ],
  },
  {
    id: '2',
    question: 'What information will I see in the results?',
    answer: 'Search results may include:',
    list: [
      'Case titles and docket numbers',
      'Court jurisdictions',
      'Names of judges and attorneys involved',
      'Argument dates and durations',
      'Audio recordings and transcripts (if available)',
    ],
  },
  {
    id: '3',
    question: 'Can I search by case name or attorney?',
    answer:
      'Yes. You can search using the case name, attorney name, or judge name. More specific searches with exact spellings may yield better results.',
  },
  {
    id: '4',
    question: 'How current is the information?',
    answer:
      'Oral argument recordings and transcripts are updated regularly, but some arguments may take time to appear depending on court procedures and processing times.',
  },
];

// Advanced Filters: Default values for Oral Arguments
export const ORAL_ARGUMENTS_FILTERS_DEFAULTS: OralArgumentsFiltersForm = {
  case_name: '',
  judge: '',
  docket_number: '',
  argued_after: '',
  argued_before: '',
  jurisdictions: createSelectedJurisdictionsForTab('federal_appellate'),
  exact_match: false,
  order_by: 'score_desc', // Default to relevance
};

// ============= JUDGE SEARCH =============

export const JUDGE_SEARCH_FAQ_ITEMS = [
  {
    id: '0',
    question: 'What is Judge Search?',
    answer:
      'Judge Search allows you to look up information about federal and state judges, including their biographical details, career history, court appointments, and educational background.',
  },
  {
    id: '1',
    question: 'What types of judges can I find?',
    answer: 'You can search for judges from various courts, including:',
    list: [
      'U.S. Supreme Court Justices',
      'Federal Circuit and District Court Judges',
      'State Supreme Court Justices',
      'State Appellate and Trial Court Judges',
      'Bankruptcy Judges',
    ],
  },
  {
    id: '2',
    question: 'What information will I see in the results?',
    answer: 'Search results may include:',
    list: [
      "Judge's full name and biographical details",
      'Current and past court positions',
      'Appointment and confirmation dates',
      'Educational background',
      'Political affiliations',
      'Links to related cases and opinions',
    ],
  },
  {
    id: '3',
    question: 'Can I search by court or appointment date?',
    answer:
      'Yes. You can filter results by specific courts, appointing authority, selection method, and date ranges for birth or appointment dates.',
  },
  {
    id: '4',
    question: 'How current is the information?',
    answer:
      'Judge information is updated regularly from official court records and biographical databases, but some details may take time to appear depending on court reporting procedures.',
  },
];

// Judge Search Sort Options
export const JUDGE_SEARCH_SORT_OPTIONS = [
  { value: 'score_desc', display: 'Relevance' },
  { value: 'name_reverse_asc', display: 'Last Name' },
  { value: 'dob_desc_name_reverse_asc', display: 'Most Recently Born' },
  { value: 'dob_asc_name_reverse_asc', display: 'Least Recently Born' },
  { value: 'dod_desc_name_reverse_asc', display: 'Most Recently Deceased' },
];

// Judge Search: Selection Method Options
export const JUDGE_SELECTION_METHOD_OPTIONS = [
  { value: '', display: '----------' },
  { value: 'e_part', display: 'Partisan Election' },
  { value: 'e_non_part', display: 'Non-Partisan Election' },
  { value: 'a_pres', display: 'Appointment (President)' },
  { value: 'a_gov', display: 'Appointment (Governor)' },
  { value: 'a_legis', display: 'Appointment (Legislature)' },
  { value: 'a_judge', display: 'Appointment (Judge)' },
  { value: 'ct_trans', display: 'Transferred (Court Restructuring)' },
];

// Judge Search: Political Affiliation Options
export const JUDGE_POLITICAL_AFFILIATION_OPTIONS = [
  { value: '', display: '----------' },
  { value: 'd', display: 'Democratic' },
  { value: 'r', display: 'Republican' },
  { value: 'i', display: 'Independent' },
  { value: 'g', display: 'Green' },
  { value: 'l', display: 'Libertarian' },
  { value: 'f', display: 'Federalist' },
  { value: 'w', display: 'Whig' },
  { value: 'j', display: 'Jeffersonian Republican' },
  { value: 'u Union', display: 'National Union' },
  { value: 'z', display: 'Reform Party' },
];

export const JUDGE_SOURCE_OPTIONS = [
  { value: 'b', display: 'Ballot' },
  { value: 'a', display: 'Appointer' },
  { value: 'o', display: 'Other' },
];

export const CLASS_ACTION_LAWSUIT_FAQ_ITEMS = [
  {
    id: '0',
    question: 'What Is a Class Action Lawsuit?',
    answer:
      'A class action allows a group of people with similar claims against a company to bring a case together, rather than filing individual lawsuits.',
  },
  {
    id: '1',
    question: 'Do I Need a Lawyer to File a Claim?',
    answer:
      'Not necessarily. In most settlements, eligible participants can submit a claim form themselves, though some people choose to hire a lawyer for guidance or to represent them in the claim.',
  },
  {
    id: '2',
    question: 'Do I have to pay to file a claim?',
    answer:
      'Most class actions work on a contingency basis, meaning attorneys are only paid if the case results in a settlement or court award.',
  },
  {
    id: '3',
    question: 'How Do I Know If I Qualify?',
    answer:
      'Eligibility depends on factors like the product or service involved, when it was used or purchased, and whether you were affected by the issue in the case.',
  },
  {
    id: '4',
    question: 'What Are the Benefits of Joining?',
    answer:
      'Joining a class action can allow you to seek compensation without filing your own lawsuit or paying legal fees upfront.',
  },
  {
    id: '5',
    question: 'Can I Choose Not To Participate?',
    answer:
      'Yes. If a settlement is approved, you may have the option to opt out and pursue your own legal claim separately.',
  },
];

export const JUDGE_SEARCH_FILTERS_DEFAULTS: JudgeSearchFiltersForm = {
  name: '',
  dob_city: '',
  dob_state: '',
  born_after: '',
  born_before: '',
  appointed_by: '',
  selection_method: '',
  political_affiliation: '',
  school: '',
  jurisdictions: createDefaultSelectedJurisdictions(),
  exact_match: false,
  order_by: 'score_desc', // Default to relevance
};

export const LEGAL_INTELLIGENCE_ROUTES = {
  BASE: '/dashboard/legal-intelligence',
} as const;

export const INTELLECTUAL_PROPERTY_ROUTES = {
  BASE: '/dashboard/intellectual-property',
} as const;

export const COURT_CASES_ROUTES = {
  BASE: '/dashboard/court-cases',
  ORAL_ARGUMENTS: '/dashboard/court-cases/oral-arguments',
  JUDGE_SEARCH: '/dashboard/court-cases/judge-search',
} as const;

export const COURT_CASES_PATHS = [
  COURT_CASES_ROUTES.BASE,
  COURT_CASES_ROUTES.ORAL_ARGUMENTS,
  COURT_CASES_ROUTES.JUDGE_SEARCH,
] as const;

// V2 Routes - 5 tabs: Cases, Opinions, Oral Arguments, Judge Search, Recalls
export const COURT_CASES_V2_ROUTES = {
  BASE: '/dashboard/court-cases',
  CASES: '/dashboard/court-cases/cases',
  CLASS_ACTION_LAWSUITS: '/dashboard/court-cases/class-action-lawsuits',
  CLASS_ACTION_LAWSUIT_REPORT:
    '/dashboard/court-cases/class-action-lawsuits/report',
  OPINIONS: '/dashboard/court-cases/opinions',
  ORAL_ARGUMENTS: '/dashboard/court-cases/oral-arguments',
  JUDGE_SEARCH: '/dashboard/court-cases/judge-search',
  RECALLS: '/dashboard/court-cases/recalls',
  RECALL_REPORT: '/dashboard/court-cases/recalls/report',
} as const;

export const COURT_CASES_V2_PATHS = [
  COURT_CASES_V2_ROUTES.CASES,
  COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUITS,
  COURT_CASES_V2_ROUTES.OPINIONS,
  COURT_CASES_V2_ROUTES.ORAL_ARGUMENTS,
  COURT_CASES_V2_ROUTES.JUDGE_SEARCH,
  COURT_CASES_V2_ROUTES.RECALLS,
] as const;

export const COURT_CASES_TAB_KEY = 'courtCasesActiveTab';

/** Query key used when linking from Legal Intelligence so headings/breadcrumbs match the hub section. */
export const LEGAL_INTEL_SECTION_QUERY_KEY = 'liSection';

export type LegalIntelSection =
  | 'legal-research'
  | 'class-actions-recalls'
  | 'intellectual-property';

export const LEGAL_INTEL_SECTION_HEADING: Record<LegalIntelSection, string> = {
  'legal-research': 'Legal Research',
  'class-actions-recalls': 'Class Actions & Recalls',
  'intellectual-property': 'Intellectual Property',
};

export function legalIntelSectionForCourtCasesPath(
  pathname: string
): LegalIntelSection {
  if (
    pathname.includes('/class-action-lawsuits') ||
    pathname.includes('/recalls')
  ) {
    return 'class-actions-recalls';
  }
  return 'legal-research';
}

export function parseLegalIntelSection(
  search: string,
  pathname: string
): LegalIntelSection {
  const raw = new URLSearchParams(search).get(LEGAL_INTEL_SECTION_QUERY_KEY);
  if (
    raw === 'legal-research' ||
    raw === 'class-actions-recalls' ||
    raw === 'intellectual-property'
  ) {
    return raw;
  }
  return legalIntelSectionForCourtCasesPath(pathname);
}

export function withLegalIntelSection(
  path: string,
  section: LegalIntelSection
): string {
  const qIndex = path.indexOf('?');
  const pathname = qIndex === -1 ? path : path.slice(0, qIndex);
  const params = new URLSearchParams(
    qIndex === -1 ? '' : path.slice(qIndex + 1)
  );
  params.set(LEGAL_INTEL_SECTION_QUERY_KEY, section);
  const serialized = params.toString();
  return serialized ? `${pathname}?${serialized}` : pathname;
}
